import { useState, useEffect } from 'react';
import api from '../../utils/api';   // custom axios instance

export default function AdminDiscounts() {
  const [coupons, setCoupons] = useState([]);
  const [form, setForm] = useState({
    code: '',
    discount: '',
    expiryDate: '',
    usageLimit: '-1',
    active: true
  });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    code: '',
    discount: '',
    expiryDate: '',
    usageLimit: '-1',
    active: true
  });

  // Fetch all coupons
  const fetchCoupons = () => {
    api.get('/api/admin/coupons')
      .then(res => setCoupons(res.data))
      .catch(() => {});
  };

  useEffect(() => {
    fetchCoupons();
  }, []);

  // Add new coupon
  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/admin/coupons', {
        code: form.code.trim().toUpperCase(),
        discount: parseFloat(form.discount),
        expiryDate: form.expiryDate || null,
        usageLimit: parseInt(form.usageLimit),
        active: form.active
      });
      setForm({ code: '', discount: '', expiryDate: '', usageLimit: '-1', active: true });
      fetchCoupons();
    } catch (err) {
      alert('Failed to add coupon');
    }
  };

  // Start editing a coupon
  const startEdit = (c) => {
    setEditingId(c.id);
    setEditForm({
      code: c.code,
      discount: c.discount.toString(),
      expiryDate: c.expiryDate ? c.expiryDate.substring(0, 16) : '',
      usageLimit: c.usageLimit.toString(),
      active: c.active
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  // Update coupon
  const handleUpdate = async (id) => {
    try {
      await api.put(`/api/admin/coupons/${id}`, {
        code: editForm.code.trim().toUpperCase(),
        discount: parseFloat(editForm.discount),
        expiryDate: editForm.expiryDate || null,
        usageLimit: parseInt(editForm.usageLimit),
        active: editForm.active
      });
      setEditingId(null);
      fetchCoupons();
    } catch (err) {
      alert('Failed to update coupon');
    }
  };

  // Delete coupon
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this coupon?')) return;
    await api.delete(`/api/admin/coupons/${id}`);
    fetchCoupons();
  };

  return (
    <div>
      <h2 className="text-xl font-bold mb-4">Coupons & Discounts</h2>

      {/* Add form */}
      <form onSubmit={handleAdd} className="mb-6 border p-4 rounded space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <input
            type="text"
            placeholder="Code (e.g. SAVE10)"
            className="border p-2 rounded"
            value={form.code}
            onChange={e => setForm({...form, code: e.target.value})}
            required
          />
          <input
            type="number"
            step="0.01"
            placeholder="Discount %"
            className="border p-2 rounded"
            value={form.discount}
            onChange={e => setForm({...form, discount: e.target.value})}
            required
          />
          <input
            type="datetime-local"
            className="border p-2 rounded"
            value={form.expiryDate}
            onChange={e => setForm({...form, expiryDate: e.target.value})}
          />
          <input
            type="number"
            placeholder="Usage limit (-1 = unlimited)"
            className="border p-2 rounded"
            value={form.usageLimit}
            onChange={e => setForm({...form, usageLimit: e.target.value})}
          />
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.active}
              onChange={e => setForm({...form, active: e.target.checked})}
            />
            Active
          </label>
          <button type="submit" className="bg-black text-white px-4 py-2 rounded">
            Add Coupon
          </button>
        </div>
      </form>

      {/* List of coupons */}
      <div className="space-y-2">
        {coupons.map(c => (
          <div key={c.id} className="border p-3 rounded flex items-center justify-between">
            {editingId === c.id ? (
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-4 gap-2">
                <input
                  type="text"
                  value={editForm.code}
                  onChange={e => setEditForm({...editForm, code: e.target.value})}
                  className="border p-1 rounded"
                />
                <input
                  type="number"
                  step="0.01"
                  value={editForm.discount}
                  onChange={e => setEditForm({...editForm, discount: e.target.value})}
                  className="border p-1 rounded"
                />
                <input
                  type="datetime-local"
                  value={editForm.expiryDate}
                  onChange={e => setEditForm({...editForm, expiryDate: e.target.value})}
                  className="border p-1 rounded"
                />
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={editForm.usageLimit}
                    onChange={e => setEditForm({...editForm, usageLimit: e.target.value})}
                    className="border p-1 rounded w-24"
                  />
                  <label className="flex items-center gap-1 text-sm">
                    <input
                      type="checkbox"
                      checked={editForm.active}
                      onChange={e => setEditForm({...editForm, active: e.target.checked})}
                    />
                    Active
                  </label>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleUpdate(c.id)}
                    className="bg-green-600 text-white px-3 py-1 rounded"
                  >
                    Save
                  </button>
                  <button
                    onClick={cancelEdit}
                    className="bg-gray-300 px-3 py-1 rounded"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div>
                  <span className="font-medium">{c.code}</span> – {c.discount}% off
                  {!c.active && <span className="text-red-500 ml-2">(inactive)</span>}
                  {c.expiryDate && (
                    <span className="text-xs text-gray-500 ml-2">
                      Expires: {new Date(c.expiryDate).toLocaleDateString()}
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => startEdit(c)} className="text-blue-500 hover:underline">
                    Edit
                  </button>
                  <button onClick={() => handleDelete(c.id)} className="text-red-500 hover:underline">
                    Delete
                  </button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}